


import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import Switch from "@mui/material/Switch";
import { axiosInstance } from "../../utility/apiInstance";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AlertConfig {
  whatsApp: any[];
  emails: any[];
  whatsAppgroups: any[];
  whatsAppmilestones: string[];
  emailsmilestones: string[];
  whatsappGroupmilestones: string[];
  selectedTemplate?: any;
  selectedOrganisation?: any;
}

export const EMPTY_ALERT_CONFIG: AlertConfig = {
  whatsApp: [],
  emails: [],
  whatsAppgroups: [],
  whatsAppmilestones: [],
  emailsmilestones: [],
  whatsappGroupmilestones: [],
  selectedTemplate: null,
  selectedOrganisation: null,
};

interface AlertConfigAccordionBodyProps {
  value: AlertConfig;
  onChange: (cfg: AlertConfig) => void;
  allContactList: any[];
  whatsappGroupList: any[];
  milestoneList: any;
  templateList: any[];
  organisationList: any[];
  onOrganisationChange?: (orgId: string | null) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function unwrapMilestones(raw: any): any[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (raw.data && Array.isArray(raw.data.data)) return raw.data.data;
  return [];
}

function milestoneName(m: any): string {
  return m?.label || m?.milestoneName || m?.name || m?.title || m?._id || "—";
}

// ── Sub-component: Milestone sub-panel ───────────────────────────────────────

type ChannelKey = "whatsApp" | "emails" | "whatsAppgroups";

interface MilestoneSubPanelProps {
  milestones: any[];
  selected: string[];
  channelLabel: string;
  onBack: () => void;
  onChange: (ids: string[]) => void;
}

function MilestoneSubPanel({
  milestones,
  selected,
  channelLabel,
  onBack,
  onChange,
}: MilestoneSubPanelProps) {
  const active = milestones.filter((m: any) => !m.isAchived);
  const allSelected =
    active.length > 0 && active.every((m: any) => selected.includes(m._id));

  const handleToggleAll = (checked: boolean) => {
    onChange(checked ? active.map((m: any) => m._id) : []);
  };

  const handleToggle = (m: any, checked: boolean) => {
    onChange(
      checked
        ? [...selected, m._id]
        : selected.filter((id) => id !== m._id)
    );
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#1a1a2e" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#1a1a2e",
    },
  };

  return (
    <div className="acab-ms-panel">
      {/* Header */}
      <div className="acab-ms-panel-header">
        <button type="button" className="acab-ms-back-btn" onClick={onBack}>
          ←
        </button>
        <span className="acab-ms-panel-title">Include / Exclude Milestones</span>
        <span className="acab-ms-panel-channel">{channelLabel}</span>
      </div>

      {/* Add all row */}
      <div className="acab-ms-row acab-ms-row--all">
        <span className="acab-ms-row-label">Add all</span>
        <Switch
          checked={allSelected}
          onChange={(e) => handleToggleAll(e.target.checked)}
          sx={switchSx}
          size="small"
        />
      </div>

      {/* Individual milestones */}
      <div className="acab-ms-list">
        {milestones.map((m: any) => {
          const isOn = selected.includes(m._id);
          const isArchived = !!m.isAchived;
          return (
            <div
              key={m._id}
              className={`acab-ms-row${isArchived ? " acab-ms-row--archived" : ""}`}
            >
              <span className="acab-ms-row-label">{milestoneName(m)}</span>
              <Switch
                checked={isOn}
                disabled={isArchived}
                onChange={(e) => handleToggle(m, e.target.checked)}
                sx={switchSx}
                size="small"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Sub-component: single channel section ─────────────────────────────────────

interface ChannelSectionProps {
  icon: string;
  title: string;
  accentClass: string;
  contactSelect: React.ReactNode;
  milestones: any[];
  selected: string[];
  onMilestoneChange: (ids: string[]) => void;
}

function ChannelSection({
  icon,
  title,
  accentClass,
  contactSelect,
  milestones,
  selected,
  onMilestoneChange,
}: ChannelSectionProps) {
  const [showMilestones, setShowMilestones] = useState(false);
  const activeCount = milestones.filter((m: any) => !m.isAchived).length;

  const milestoneHint =
    selected.length === 0
      ? "Include/Exclude milestone"
      : `Include/Exclude milestone ${selected.length} / ${activeCount} selected`;

  return (
    <div className={`acab-channel-card ${accentClass}`}>
      {!showMilestones ? (
        <>
          <div className="acab-card-header">
            <span className="acab-card-icon">{icon}</span>
            <span className="acab-card-title">{title}</span>
            {selected.length > 0 && (
              <span className="acab-badge acab-badge--ms">
                ✓ {selected.length}/{activeCount}
              </span>
            )}
          </div>
          <div className="acab-card-body-col">
            <div className="acab-card-contacts">{contactSelect}</div>
            <button
              type="button"
              className="acab-ms-trigger"
              onClick={() => setShowMilestones(true)}
            >
              <span className="acab-ms-trigger-text">{milestoneHint}</span>
              <span className="acab-ms-trigger-chevron">▼</span>
            </button>
          </div>
        </>
      ) : (
        <MilestoneSubPanel
          milestones={milestones}
          selected={selected}
          channelLabel={title}
          onBack={() => setShowMilestones(false)}
          onChange={onMilestoneChange}
        />
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AlertConfigAccordionBody({
  value,
  onChange,
  allContactList,
  whatsappGroupList,
  milestoneList,
  templateList,
  organisationList,
  onOrganisationChange,
}: AlertConfigAccordionBodyProps) {
  const [filteredContacts, setFilteredContacts] = useState<any[]>(
    allContactList || []
  );
  const [loadingContacts, setLoadingContacts] = useState(false);

  // ── KEY FIX: keep a ref to the latest `value` so async/handler callbacks
  //    never close over a stale copy. ────────────────────────────────────────
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });

  useEffect(() => {
    if (!value.selectedOrganisation) {
      setFilteredContacts(allContactList || []);
    }
  }, [allContactList]);

  // Always read latest value from the ref, then spread the patch on top.
  const set = (patch: Partial<AlertConfig>) =>
    onChange({ ...valueRef.current, ...patch });

  // ── Template apply ────────────────────────────────────────────────────────
  const handleTemplateChange = (option: any) => {
    if (!option) {
      set({ selectedTemplate: null });
      return;
    }
    const tpl = (templateList || []).find((t: any) => t._id === option.value);
    if (!tpl) {
      set({ selectedTemplate: option });
      return;
    }
    set({
      selectedTemplate: option,
      whatsApp: (tpl.waContacts || []).map((c: any) => ({
        label: `${c.salutation}. ${c.name} (${c.countryCode}${c.whatappNumber}${
          c.masterContactOrganization?.name
            ? ", " + c.masterContactOrganization.name
            : ""
        })`,
        value: c._id,
      })),
      emails: (tpl.emailContacts || []).map((c: any) => ({
        label: `${c.salutation}. ${c.name} (${c.email}${
          c.masterContactOrganization?.name
            ? ", " + c.masterContactOrganization.name
            : ""
        })`,
        value: c._id,
      })),
      whatsAppgroups: (tpl.whatsappGroups || []).map((g: any) => ({
        label: g.name,
        value: g._id,
      })),
      whatsAppmilestones: (tpl.waMilestones || []).map((m: any) => m._id),
      emailsmilestones: (tpl.emailMilestones || []).map((m: any) => m._id),
      whatsappGroupmilestones: (tpl.whatsappGroupMilestones || []).map(
        (m: any) => m._id
      ),
    });
  };

  // ── Organisation filter ───────────────────────────────────────────────────
  // ✅ No useCallback needed — valueRef always gives us the latest value,
  //    so there is no stale-closure risk regardless of re-renders.
  const handleOrganisationChange = async (option: any) => {
    // Patch only selectedOrganisation; everything else stays intact
    // because `set` spreads over valueRef.current (always fresh).
    set({ selectedOrganisation: option });
    onOrganisationChange?.(option?.value || null);

    if (!option) {
      setFilteredContacts(allContactList || []);
      return;
    }

    setLoadingContacts(true);
    try {
      const res = await axiosInstance.get(`/api/v1/contact/${option.value}`);
      setFilteredContacts(res.data?.data || []);
    } catch {
      setFilteredContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // ── Derived lists ─────────────────────────────────────────────────────────
  const milestones = unwrapMilestones(milestoneList);

  const waContacts = filteredContacts
    .filter(
      (c: any) =>
        c.isActive &&
        c.whatappNumber &&
        (c.allowedChannels || []).includes("whatsapp")
    )
    .map((c: any) => ({
      label: `${c.salutation}. ${c.name} (${c.countryCode}${c.whatappNumber}${
        c.masterContactOrganization?.name
          ? ", " + c.masterContactOrganization.name
          : ""
      })`,
      value: c._id,
    }));

  const emailContacts = filteredContacts
    .filter(
      (c: any) =>
        c.isActive &&
        c.email &&
        (c.allowedChannels || []).includes("email")
    )
    .map((c: any) => ({
      label: `${c.salutation}. ${c.name} (${c.email}${
        c.masterContactOrganization?.name
          ? ", " + c.masterContactOrganization.name
          : ""
      })`,
      value: c._id,
    }));

  const groupOptions = (whatsappGroupList || [])
    .filter((g: any) => g?.name)
    .map((g: any) => ({ label: g.name, value: g._id }));

  const templateOptions = (templateList || []).map((t: any) => ({
    label: t.templateName,
    value: t._id,
  }));

  const orgOptions = (organisationList || []).map((o: any) => ({
    label: o.name,
    value: o._id,
  }));

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: "#fff",
      border: "1px solid #e8eaed",
      borderRadius: 6,
      boxShadow: "none",
      minHeight: 34,
      fontSize: 12,
      "&:hover": { borderColor: "#c0c4cc" },
    }),
    placeholder: (base: any) => ({ ...base, color: "#b0b4bc", fontSize: 12 }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "#eef2ff",
      borderRadius: 4,
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "#4f46e5",
      fontSize: 11,
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: "#4f46e5",
      ":hover": { backgroundColor: "#e0e7ff", color: "#3730a3" },
    }),
    singleValue: (base: any) => ({ ...base, fontSize: 12, color: "#1a1a2e" }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      backgroundColor: state.isSelected
        ? "#eef2ff"
        : state.isFocused
        ? "#f5f7ff"
        : "white",
      color: state.isSelected ? "#4f46e5" : "#374151",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="acab-root">

      {/* ══ ROW 1: Template | Organisation ══════════════════════════════════ */}
      <div className="acab-row1">

        <div className="acab-row1-col">
          <div className="acab-row1-header">
            <span className="acab-row1-icon">📄</span>
            <span className="acab-row1-title">Template</span>
            {value.selectedTemplate && (
              <span className="acab-badge acab-badge--tpl">✓ Applied</span>
            )}
          </div>
          <Select
            isClearable
            placeholder="Choose a template…"
            options={templateOptions}
            value={value.selectedTemplate}
            onChange={handleTemplateChange}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {value.selectedTemplate && (
            <p className="acab-row1-hint">
              All channels pre-filled. Edit below to override.
            </p>
          )}
        </div>

        <div className="acab-row1-col">
          <div className="acab-row1-header">
            <span className="acab-row1-icon">🏢</span>
            <span className="acab-row1-title">Organisation</span>
            {value.selectedOrganisation && (
              <span className="acab-badge acab-badge--org">✓ Filtered</span>
            )}
          </div>
          <Select
            isClearable
            isLoading={loadingContacts}
            placeholder="Filter contacts by org…"
            options={orgOptions}
            value={value.selectedOrganisation}
            onChange={handleOrganisationChange}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {value.selectedOrganisation && (
            <p className="acab-row1-hint">
              Showing contacts from {value.selectedOrganisation.label} only.
            </p>
          )}
        </div>
      </div>

      {/* ══ ROW 2: Channel cards ═════════════════════════════════════════════ */}
      <div className="acab-row2">

        <ChannelSection
          icon="💬"
          title="WhatsApp"
          accentClass="acab-card--wa"
          contactSelect={
            <Select
              isMulti
              placeholder="Select contacts…"
              options={waContacts}
              value={value.whatsApp}
              onChange={(opt: any) => set({ whatsApp: opt || [] })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          }
          milestones={milestones}
          selected={value.whatsAppmilestones}
          onMilestoneChange={(ids) => set({ whatsAppmilestones: ids })}
        />

        <ChannelSection
          icon="✉️"
          title="Email"
          accentClass="acab-card--email"
          contactSelect={
            <Select
              isMulti
              placeholder="Select contacts…"
              options={emailContacts}
              value={value.emails}
              onChange={(opt: any) => set({ emails: opt || [] })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          }
          milestones={milestones}
          selected={value.emailsmilestones}
          onMilestoneChange={(ids) => set({ emailsmilestones: ids })}
        />

        <ChannelSection
          icon="👥"
          title="WA Groups"
          accentClass="acab-card--group"
          contactSelect={
            <Select
              isMulti
              placeholder="Select groups…"
              options={groupOptions}
              value={value.whatsAppgroups}
              onChange={(opt: any) => set({ whatsAppgroups: opt || [] })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          }
          milestones={milestones}
          selected={value.whatsappGroupmilestones}
          onMilestoneChange={(ids) => set({ whatsappGroupmilestones: ids })}
        />

      </div>
    </div>
  );
}